import type { ITeamJSON, IOrganizationJSON, IOrganizationSimpleJSON, IOrganizationUserSimpleJSON } from 'external-api-types';
import type { AddNull } from '../../modules/ts.utils';
import { apiFetch } from '../ApiClient';
import type { OrganizationRoleOptions } from './RoleAssignment';

export type OrganizationRoleOptionsRequest = AddNull<OrganizationRoleOptions>;

export function getOrganizationList(): Promise<IOrganizationSimpleJSON[]> {
    return apiFetch(`/organization/list`, null, 'get');
}

export function getOrganization(orgId: string): Promise<IOrganizationJSON> {
    return apiFetch(`/organization/${orgId}`, null, 'get');
}

export function assignUserToOrganizationRole({
    orgId,
    id,
    role,
    options
}: {
    orgId: string;
    id: string;
    role: string;
    options?: OrganizationRoleOptionsRequest;
}): Promise<void> {
    return apiFetch(`/organization/${orgId}/role/user/${id}`, { role, options }, 'put');
}

export function removeUserOrganizationRole({ orgId, id, role }: { orgId: string; id: string; role: string }): Promise<void> {
    return apiFetch(`/organization/${orgId}/role/user/${id}`, { role }, 'delete');
}

export function assignTeamToOrganizationRole({ orgId, id, role }: { orgId: string; id: string; role: string }): Promise<void> {
    return apiFetch(`/organization/${orgId}/role/team/${id}`, { role }, 'put');
}

export function removeTeamOrganizationRole({ orgId, id, role }: { orgId: string; id: string; role: string }): Promise<void> {
    return apiFetch(`/organization/${orgId}/role/team/${id}`, { role }, 'delete');
}

export function addProjectToOrganization(orgId: string, projectId: string): Promise<void> {
    return apiFetch(`/organization/${orgId}/project/${projectId}`, null, 'patch');
}

export type OrganizationUser = IOrganizationUserSimpleJSON & { teams?: OrganizationTeam[] };
export type OrganizationTeam = ITeamJSON & { users?: OrganizationUser[] };
export type UserTeamRelations = {
    teamById: Record<string, OrganizationTeam>;
    userById: Record<string, OrganizationUser>;
};

export function deriveUserTeamRelations(users: IOrganizationUserSimpleJSON[], teams: ITeamJSON[]): UserTeamRelations {
    const teamById = {} as Record<string, OrganizationTeam>;
    const userById = {} as Record<string, OrganizationUser>;
    teams?.forEach((team) => {
        if (team.id) {
            teamById[team.id] = team;
        }
    });
    users?.forEach((user) => {
        const userId = user.id;
        if (userId) {
            userById[userId] = user;
            user.teamIds?.forEach((teamId) => {
                const team = teamById[teamId];
                if (!team) return;

                if (!userById[userId]!.teams) {
                    userById[userId]!.teams = [];
                }
                userById[userId]!.teams!.push(team);

                if (!team.users) {
                    team.users = [];
                }
                team.users!.push(user);
            });
        }
    });

    return { teamById, userById };
}

function emailToEmailDomain(email?: string): string | undefined {
    // We are taking the last part, not the second part, for a good reason.
    // Do not change to emailParts[1];
    // https://stackoverflow.com/a/12355882
    const emailParts = email?.split('@');
    if (emailParts) {
        return emailParts[emailParts.length - 1];
    }
    return undefined;
}

export const isEmailInEmailDomains = (emailDomains: string[], email: string): boolean => {
    const emailDomain = emailToEmailDomain(email);
    if (!emailDomain) {
        return false;
    }

    return emailDomains.includes(emailDomain);
};
